import { useState, useCallback, useRef, useEffect } from "react";
import LPDConfirmation from "../components/LPDConfirmation";
import { useLocation } from "react-router-dom";
import PortalPopup from "../components/PortalPopup";
import NameInput from "../components/NameInput";
import AddressInput from "../components/AddressField";
import AmountDropdown from "../components/AmountDropdown";
import DatePickerField from "../components/DatePickerField";
import EmailInputField from "../components/EmailInputField";
import { Upload, Pen, Send } from "lucide-react";

const LPDAutoPopulate = ({ userId }) => {
  const [isLPDConfirmationOpen, setLPDConfirmationOpen] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("£");
  const [dueDate, setDueDate] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [houseName, setHouseName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [isDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [formData, setFormData] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const token = localStorage.getItem("authToken");

  const currencies = [
    { code: "GBP", name: "British Pound",    symbol: "£" },
    { code: "EUR", name: "Euro",             symbol: "€" },
    { code: "INR", name: "Indian Rupee",     symbol: "₹" },
    { code: "CAD", name: "Canadian Dollar",  symbol: "$" },
    { code: "AUD", name: "Australian Dollar",symbol: "$" },
    { code: "USD", name: "US Dollar",        symbol: "$" },
  ];

  const handleAmountChange   = (v) => setAmount(v);
  const handleDateChange     = (d) => setDueDate(d);
  const handleCurrencyChange = (s) => setCurrency(s);
  const handleEmailChange    = (e) => setEmail(e.target.value);

  const handleButtonClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      const reader = new FileReader();
      reader.onload = (event) => setSignatureImage(event.target.result);
      reader.readAsDataURL(file);
    } else {
      alert("Please select a valid image file (JPEG or PNG)");
    }
  };

  const fetchInvoice = async (uid) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/get-invoice/${uid}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setInvoiceNumber(data?.invoiceNumbers || "Not found");
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  useEffect(() => { fetchInvoice(userId); }, []);

  const openLPDConfirmation = useCallback(() => {
    if (isLoading) return;
    const currentFormData = {
      name, businessName, invoiceNumber, invoiceId: "",
      email, dueDate, amount, currency,
      houseName, street, city, country, postcode, signatureImage,
    };
    setFormData(currentFormData);
    setLPDConfirmationOpen(true);
    setIsLoading(true);
    handleLpdUpdate(currentFormData).finally(() => setIsLoading(false));
  }, [name, businessName, invoiceNumber, dueDate, amount, email, currency, houseName, street, city, country, postcode, isLoading]);

  const handleLpdUpdate = async (fd) => {
    const updatedData = {
      userId: userId || "",
      name: fd.name || "", businessName: fd.businessName || "",
      invoiceNumber: fd.invoiceNumber || "", dueDate: fd.dueDate || "",
      amount: fd.amount || "", email: fd.email || "", status: "Sent",
      address: { house: fd.houseName || "", street: fd.street || "", city: fd.city || "", country: fd.country || "", postalCode: fd.postcode || "" },
    };
    try {
      const response = await fetch(`${API_URL}/api/lpd/save_lpd_form`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(updatedData),
      });
      if (!response.ok) console.error("Error updating details:", await response.text());
    } catch (error) {
      console.error("Error updating details:", error);
    }
  };

  const closeLPDConfirmation = useCallback(() => setLPDConfirmationOpen(false), []);

  const fieldGroups = [
    { label: "Full name",              placeholder: "John Clark",                   value: name,          onChange: (e) => setName(e.target.value) },
    { label: "Business name",          placeholder: "Business Name",                value: businessName,  onChange: (e) => setBusinessName(e.target.value) },
    { label: "Invoice number",         placeholder: "INV-0001",                     value: invoiceNumber, onChange: (e) => setInvoiceNumber(e.target.value) },
  ];

  const addressFields = [
    { label: "Postcode",              placeholder: "Enter your postcode",              value: postcode,  onChange: (e) => setPostcode(e.target.value) },
    { label: "House / Building name", placeholder: "Enter your house / building name", value: houseName, onChange: (e) => setHouseName(e.target.value) },
    { label: "Street",                placeholder: "Enter your street",                value: street,    onChange: (e) => setStreet(e.target.value) },
    { label: "Town / City",           placeholder: "Enter your town / city",           value: city,      onChange: (e) => setCity(e.target.value) },
    { label: "Country",               placeholder: "Enter your country",               value: country,   onChange: (e) => setCountry(e.target.value) },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');

        .lpd-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

        .lpd-panel {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
        }

        .lpd-panel-header {
          padding: 18px 24px;
          border-bottom: 1px solid #E2E8F0;
          background: #FAFBFD;
          border-radius: 16px 16px 0 0;
        }

        .lpd-section-title {
          font-size: 13px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          margin: 0 0 16px 0;
        }

        .lpd-field-group {
          display: flex; flex-direction: column; gap: 6px;
        }

        .lpd-label {
          font-size: 12px; font-weight: 600; color: #475569;
        }

        .lpd-divider {
          height: 1px; background: #F1F5F9; margin: 8px 0;
        }

        .lpd-sig-box {
          background: #F8FAFC; border: 1.5px dashed #CBD5E1; border-radius: 10px;
          padding: 16px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: border-color 0.15s, background 0.15s;
          min-height: 72px;
        }
        .lpd-sig-box:hover { border-color: #2563EB; background: #EFF6FF; }

        .lpd-send-btn {
          width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border: none; border-radius: 10px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700;
          color: #ffffff; cursor: pointer;
          background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
          box-shadow: 0 2px 8px rgba(37,99,235,0.3);
          transition: opacity 0.15s, transform 0.15s;
        }
        .lpd-send-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .lpd-send-btn:disabled { background: #93C5FD; cursor: not-allowed; box-shadow: none; transform: none; }

        .lpd-doc-text { font-size: 13px; color: #1E293B; line-height: 1.8; }
        .lpd-doc-strong { font-weight: 700; color: #0F172A; }
        .lpd-doc-notice {
          background: #FFFBEB; border: 1px solid #FCD34D; border-radius: 8px;
          padding: 12px 16px; font-size: 13px; color: #92400E; font-weight: 600;
          line-height: 1.6;
        }
        .lpd-amount-highlight {
          font-family: 'DM Mono', monospace; font-weight: 700; color: #DC2626;
        }

        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="lpd-root" style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 24, alignItems: "start" }}>

        {/* ── Left panel: form ── */}
        <div className="lpd-panel">
          <div className="lpd-panel-header">
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>LPD form details</div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Fill in the details below to generate the notice.</div>
          </div>

          <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Name / Business / Invoice */}
            <p className="lpd-section-title" style={{ marginBottom: 0 }}>Recipient</p>
            {fieldGroups.map((f) => (
              <div key={f.label} className="lpd-field-group">
                <NameInput title={f.label} placeholder={f.placeholder} width="100%" value={f.value} onChange={f.onChange} />
              </div>
            ))}

            <div className="lpd-divider" />

            {/* Date & Amount */}
            <p className="lpd-section-title" style={{ marginBottom: 0 }}>Invoice details</p>
            <div className="lpd-field-group">
              <DatePickerField selectedDate={dueDate} value={dueDate} onChange={handleDateChange} />
            </div>
            <div className="lpd-field-group">
              <span className="lpd-label">Amount</span>
              <AmountDropdown currencies={currencies} onCurrencyChange={handleCurrencyChange} onAmountChange={handleAmountChange} />
            </div>

            <div className="lpd-divider" />

            {/* Email */}
            <p className="lpd-section-title" style={{ marginBottom: 0 }}>Contact</p>
            <div className="lpd-field-group">
              <EmailInputField
                title="Email"
                placeholder="name@yourcompany.com"
                value={email}
                onChange={handleEmailChange}
                validationMessage="Please provide a valid email address."
                iconColor="#D93C3D"
                iconSize="20px"
                bgColorLight="#FFFFFF"
                bgColorDark="#0C111D"
                isDarkMode={isDarkMode}
              />
            </div>

            <div className="lpd-divider" />

            {/* Address */}
            <p className="lpd-section-title" style={{ marginBottom: 0 }}>Address</p>
            {addressFields.map((f) => (
              <div key={f.label} className="lpd-field-group">
                <AddressInput
                  label={f.label}
                  placeholder={f.placeholder}
                  helpIcon="assets/help-circle.svg"
                  value={f.value}
                  onChange={f.onChange}
                />
              </div>
            ))}

            <div className="lpd-divider" />

            {/* Signature */}
            <p className="lpd-section-title" style={{ marginBottom: 0 }}>Signature</p>
            <div className="lpd-sig-box" onClick={handleButtonClick}>
              {signatureImage ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: "100%" }}>
                  <img src={signatureImage} alt="Signature" style={{ maxHeight: 56, objectFit: "contain" }} />
                  <span style={{ fontSize: 11, color: "#94A3B8" }}>Click to change</span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748B", fontSize: 13 }}>
                  <Pen size={16} style={{ color: "#94A3B8" }} />
                  <span>Click to upload signature</span>
                  <Upload size={14} style={{ color: "#94A3B8" }} />
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg,image/png" style={{ display: "none" }} />
            </div>

            {/* Send button */}
            <button className="lpd-send-btn" onClick={openLPDConfirmation} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                  Sending…
                </>
              ) : (
                <><Send size={16} />Send notice</>
              )}
            </button>
          </div>
        </div>

        {/* ── Right panel: document preview ── */}
        <div className="lpd-panel" style={{ padding: 32 }}>

          {/* Letterhead */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 20, borderBottom: "1px solid #E2E8F0", marginBottom: 24 }}>
            <img src="/assets/main-logo.svg" alt="Company Logo" style={{ height: 48, width: "auto" }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", textAlign: "right" }}>Payment Demand</div>
          </div>

          {/* Recipient address block */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div className="lpd-doc-text" style={{ textAlign: "right" }}>
                {businessName && <div className="lpd-doc-strong">{businessName}</div>}
                {houseName && <div>{houseName}</div>}
                {street && <div>{street}</div>}
                {city && <div>{city}</div>}
                {country && <div>{country}</div>}
                {postcode && <div>{postcode}</div>}
                {!businessName && !houseName && !street && !city && !country && !postcode && (
                  <div style={{ color: "#CBD5E1", fontStyle: "italic" }}>Recipient address will appear here</div>
                )}
              </div>
            </div>
          </div>

          {/* Salutation & subject */}
          <div style={{ marginBottom: 20 }} className="lpd-doc-text">
            <div className="lpd-doc-strong" style={{ marginBottom: 4 }}>
              Dear {name || <span style={{ color: "#CBD5E1", fontStyle: "italic" }}>Recipient name</span>}
            </div>
            <div className="lpd-doc-strong">
              Re: Invoice Number: {invoiceNumber || <span style={{ color: "#CBD5E1", fontStyle: "italic", fontWeight: 400 }}>Invoice number</span>}
            </div>
          </div>

          {/* Notice body */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
            <div className="lpd-doc-notice">
              NOTICE OF ATTENTION TO COMMENCE LEGAL PROCEEDINGS pursuant to the Late Payment of Commercial Debts (Interest) Act 1998 as amended and supplemented by the Late Payment of Commercial Debt Regulations 2002 and 2013.
            </div>

            <p className="lpd-doc-text" style={{ margin: 0 }}>
              We hereby give you notice that our client intends to commence Country Court proceedings which will include a claim for late payment compensation, accrued interest and the reasonable cost of this Solicitor's Letter.
            </p>

            <p className="lpd-doc-text" style={{ margin: 0 }}>
              The sum now owing to our client is{" "}
              <span className="lpd-amount-highlight">{currency} {amount || "—"}</span>{" "}
              together with late payment compensation of £40.00, interest of £4.10 and £25.00 reasonable Solicitor's Letter costs claimable under Regulation 3 of the Late Payment of Commercial Debt Regulations 2013.
            </p>

            <p className="lpd-doc-text" style={{ margin: 0 }}>
              Take notice that unless our client receives payment for the total sum of{" "}
              <span className="lpd-amount-highlight">{currency} {amount || "—"}</span>{" "}
              within <strong>7 days</strong> from the date of this letter, proceedings will be issued for the whole balance outstanding together with continuing interest and Court costs without further notice to you.
            </p>
          </div>

          {/* Closing */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p className="lpd-doc-text" style={{ margin: 0 }}>Yours faithfully,</p>

            {/* Signature box */}
            <div
              style={{
                width: 180, minHeight: 72, border: "1.5px dashed #CBD5E1", borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "#F8FAFC", cursor: "pointer", padding: 12, transition: "border-color 0.15s",
              }}
              onClick={handleButtonClick}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#2563EB"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = "#CBD5E1"}
            >
              {signatureImage ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: "100%" }}>
                  <img src={signatureImage} alt="Signature" style={{ maxHeight: 48, objectFit: "contain" }} />
                  <span style={{ fontSize: 10, color: "#94A3B8" }}>Click to change</span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#94A3B8", fontSize: 12 }}>
                  <Pen size={14} />
                  <span>Signature</span>
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg,image/png" style={{ display: "none" }} />
            </div>

            <p className="lpd-doc-strong" style={{ margin: 0, fontSize: 13 }}>Company name</p>
          </div>
        </div>
      </div>

      {isLPDConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLPDConfirmation}>
          <LPDConfirmation
            onClose={closeLPDConfirmation}
            formData={{ name, businessName, invoiceNumber, invoiceId: "", email, dueDate, amount, currency, houseName, street, city, country, postcode, signatureImage }}
          />
        </PortalPopup>
      )}
    </>
  );
};

export default LPDAutoPopulate;